import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { round } from "lodash";
import routes from "routes/routes";
import { useAppSelector } from "store/hooks";
import { userMetadataSelector } from "store/selectors";

import { BankAccountVerifyStatus } from "types/BankAccountVerifyStatus";
import { showErrorModal } from "helpers";
import { useBankAccountLink } from "hooks";
import { IBankAccountsSelectProps } from "components/BankAccountsSelect/BankAccountsSelect";

import {
  CreateWithdrawalTransactionDto,
  GetPlaidBankAccountsResponseDto,
  mutationWithdrawalControllerCreateWithdrawalTransaction,
  queryBankCardsControllerGetSyncteraAccount,
  SyncteraAccountDto,
  WithdrawalTransactionDestinationDto,
} from "utils/swagger_react_query";

import { TransferFundsFormType } from "./components";
import { ManualBankAccountAdditionalInfoFormType } from "./components/TransferModals/validationSchema";

interface IBankNumbersState {
  accountNumberHidden: boolean;
  routingNumberHidden: boolean;
}

export const useEmployeeTransferFundsPage = () => {
  const translationPrefix = `banking_pages.employee.transfer_funds_page`;
  const navigate = useNavigate();
  const currentUser = useAppSelector(userMetadataSelector);

  const [isLoading, setLoading] = useState<boolean>(true);
  const [isLoadingBankAccounts, setLoadingBankAccounts] = useState<boolean>(true);
  const [selectedBankAccount, setSelectedBankAccount] = useState<GetPlaidBankAccountsResponseDto>();
  const [svoc, setSvoc] = useState<boolean>(false);

  const [bankAccountDetails, setBankAccountDetails] = useState<SyncteraAccountDto>();

  const [transferConfirmationModal, setTransferConfirmationModal] = useState<boolean>(false);
  const [transferSuccessModal, setTransferSuccessModal] = useState<boolean>(false);
  const [manualBankAccountAdditionalInfoModal, setManualBankAccountAdditionalInfoModal] =
    useState<boolean>(false);
  const [bankAccountNumbersState, setBankAccountNumbersState] = useState<IBankNumbersState>({
    accountNumberHidden: true,
    routingNumberHidden: true,
  });
  const [transactionData, setTransactionData] =
    useState<Partial<WithdrawalTransactionDestinationDto>>();

  const { metadata, data, actions } = useBankAccountLink({
    loadingCallback: setLoadingBankAccounts,
  });

  const initFetch = async () => {
    try {
      const bankAccountsRes = await queryBankCardsControllerGetSyncteraAccount();
      setBankAccountDetails(bankAccountsRes);
    } catch (error) {
      showErrorModal(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.userId) {
      initFetch();
    }
  }, []);

  useEffect(() => {
    if (data.bankAccounts?.length) {
      const defaultAccount = data.bankAccounts?.find((it) => it.isDefault);

      setSelectedBankAccount(defaultAccount || data.bankAccounts?.[0]);
    }
  }, [data.bankAccounts]);

  const handleSelectBankAccount = (account: GetPlaidBankAccountsResponseDto) => {
    setSelectedBankAccount(account);
  };

  const handleOpenConfirmationModal = (
    transactionValue?: TransferFundsFormType,
    additionalBankInfo?: ManualBankAccountAdditionalInfoFormType,
  ) => {
    setSvoc(true);
    if (transactionValue) {
      setTransactionData({
        amount: Number(transactionValue?.amount),
        description: transactionValue?.description,
      });
    }

    if (selectedBankAccount?.verificationStatus === BankAccountVerifyStatus.MANUALLY_VERIFIED) {
      if (additionalBankInfo) {
        setTransactionData({
          ...transactionData,
          manualBankName: additionalBankInfo?.bankName || "",
          manualOwnerName: additionalBankInfo?.bankHolderName || "",
        });
        setManualBankAccountAdditionalInfoModal(false);
        setTransferConfirmationModal(true);
      } else {
        setManualBankAccountAdditionalInfoModal(true);
      }
    } else {
      setTransferConfirmationModal(true);
    }
  };

  const handleSubmitTransferFunds = async () => {
    try {
      setTransferConfirmationModal(false);
      setLoading(true);
      const values = transactionData;
      const payload: CreateWithdrawalTransactionDto = {
        destinations: [
          {
            amount: round(Number(values?.amount) * 100),
            description: values?.description || undefined,
            bankAccountId: selectedBankAccount?.bankAccountId || "",
            bankAccountVendor: "PLAID",
            manualBankName: values?.manualBankName || undefined,
            manualOwnerName: values?.manualOwnerName || undefined,
          },
        ],
      };

      await mutationWithdrawalControllerCreateWithdrawalTransaction()(payload);
      await initFetch();
      await actions.refetchBankAccountsList();
      setTransactionData(undefined);
      setTransferSuccessModal(true);
    } catch (error) {
      showErrorModal(error);
      setLoading(false);
    }
  };

  const redirectToBankingPage = () => {
    navigate(routes.EMPLOYEE_BANKING);
  };

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      redirectToBankingPage();
      return;
    }
  };

  const handleConfirmationModal = (state: boolean) => {
    if (state) {
      setTransferConfirmationModal(state);
    } else {
      setTransferConfirmationModal(false);
      setTransactionData(undefined);
    }
  };

  const bankAccountSelectProps: IBankAccountsSelectProps = {
    bankAccountsList: data.bankAccounts || [],
    selectedBankAccount: selectedBankAccount,
    onChange: handleSelectBankAccount,
    onAddAccount: actions.openModal,
    onVerify: actions.handleVerifyBankAccount,
  };

  return {
    metadata: {
      isLoading,
      isLoadingBankAccounts,
      translationPrefix,
      svoc,
      setSvoc,
    },
    pageData: {
      paymentDetails: {
        bankAccountSelectProps,
      },
      bankAccountDetails: {
        bankAccountDetails,
        bankAccountNumbersState,
        setBankAccountNumbersState,
      },
    },
    actions: {
      handleSubmitTransferFunds,
      handleBack,
      handleOpenConfirmationModal,
    },
    modals: {
      transferConfirmationModal: {
        isOpen: transferConfirmationModal,
        setModal: handleConfirmationModal,
      },
      transferSuccessModal: {
        isOpen: transferSuccessModal,
        setModal: setTransferSuccessModal,
      },
      manualBankAccountAdditionalInfoModal: {
        isOpen: manualBankAccountAdditionalInfoModal,
        setModal: setManualBankAccountAdditionalInfoModal,
      },
    },
  };
};
